import { Flex, HStack, Icon, Text, VStack } from "@chakra-ui/react";
import { Flag, UserMinus, UserPlus, X } from "lucide-react";
import { memo, useMemo } from "react";
import { Button } from "components/ds";
import type { MFReviewAccount } from "state/members/accessReviews";
import ChangeCard from "./ChangeCard";
import { appliedCorrectionCards, type EntitlementChange } from "../reviewLogic";

const accountLabel = (a: MFReviewAccount): string =>
  a.member.name ||
  a.member.email ||
  a.identity?.displayName ||
  a.identity?.extId ||
  "Unknown account";

/**
 * The account's flagged-for-adjustment entitlements, as read-only records. These
 * are flags raised for the remediation task — NOT entitlement corrections — so
 * they render in their own clearly-separated section.
 */
export const deriveFlaggedRecords = (
  accounts: MFReviewAccount[],
): EntitlementChange[] => {
  const out: EntitlementChange[] = [];
  for (const a of accounts) {
    const flagged = (a.entitlements || []).filter((e) => e.flaggedForAdjustment);
    if (flagged.length === 0) continue;
    out.push({
      id: a.id,
      accountId: a.id,
      accountLabel: accountLabel(a),
      added: [],
      removed: flagged.map((e) => e.snapshotEntitlementName),
      applied: false,
    });
  }
  return out;
};

/**
 * Accounts removed because they don't exist at source (PLO-4803): the
 * remove-non-existent correction stamps `review: "removed"` + status
 * `already_removed`. These show as applied fixes — the access was revoked and
 * the account dropped out of the review.
 */
export const deriveRemovedRecords = (
  accounts: MFReviewAccount[],
): EntitlementChange[] =>
  accounts
    .filter((a) => (a.review as string) === "removed")
    .map((a) => ({
      id: a.id,
      accountId: a.id,
      accountLabel: accountLabel(a),
      added: [],
      removed: ["Access revoked, not in source"],
      applied: true,
    }));

/**
 * FixPanel — the "Fix incorrect data" split panel (PLO-4803).
 *
 * Corrections auto-save and persist server-side, so the panel is a read-only
 * record derived entirely from the accounts payload, in three clearly-separated
 * sections:
 *  - Applied corrections: entitlement fixes from the server's
 *    `entitlementCorrections` list (Adding/Removing chips, "Applied" badge,
 *    corrected-by + date).
 *  - Flagged for removal: the account's flagged-for-adjustment entitlements —
 *    a record feeding the remediation task, NOT a fix.
 *  - Removed — not in source: accounts marked as no longer existing at source.
 */

export interface FixPanelProps {
  accounts: MFReviewAccount[];
  readOnly?: boolean;
  onAddUser: () => void;
  onClose: () => void;
}

const FixPanel = memo(
  ({ accounts, readOnly, onAddUser, onClose }: FixPanelProps) => {
    const flaggedRecords = useMemo(
      () => deriveFlaggedRecords(accounts),
      [accounts],
    );

    const removedRecords = useMemo(
      () => deriveRemovedRecords(accounts),
      [accounts],
    );

    const correctionCards = useMemo(
      () => appliedCorrectionCards(accounts),
      [accounts],
    );

    const hasCorrections = correctionCards.length > 0;
    const hasFlagged = flaggedRecords.length > 0;
    const hasRemoved = removedRecords.length > 0;
    const totalCount =
      correctionCards.length + flaggedRecords.length + removedRecords.length;
    const isEmpty = !hasCorrections && !hasFlagged && !hasRemoved;

    return (
      <Flex
        direction="column"
        h="100%"
        bg="white"
        borderWidth="1px"
        borderColor="gray.200"
        borderRadius="12px"
        overflow="hidden"
      >
        {/* header */}
        <Flex
          align="center"
          justify="space-between"
          gap={2}
          px={5}
          py={4}
          flex="none"
          borderBottom="1px solid"
          borderColor="gray.200"
        >
          <VStack align="flex-start" spacing={0.5} minW={0}>
            <Text fontSize="lg" fontWeight={600} color="gray.900">
              Fix incorrect data
            </Text>
            <Text fontSize="xs" color="gray.500">
              {totalCount} {totalCount === 1 ? "item" : "items"}
            </Text>
          </VStack>
          <Button
            aria-label="Close fix panel"
            icon={<Icon as={X} boxSize={4} />}
            size="sm"
            variant="subtle"
            tone="neutral"
            onClick={onClose}
          />
        </Flex>

        {/* body */}
        <VStack
          align="stretch"
          flex="1"
          overflowY="auto"
          spacing={5}
          px={5}
          py={4}
        >
          {isEmpty ? (
            <VStack spacing={4} py={8} align="center">
              <Text fontSize="sm" color="gray.500" textAlign="center">
                No fixes recorded yet. When the source system disagrees with
                Ploy, fix it here: use "Fix incorrect data" on a row or
                selection (fixes apply immediately), flag an account's
                entitlements, or add a user Ploy is missing.
              </Text>
              {!readOnly && (
                <Button
                  size="sm"
                  variant="outline"
                  tone="primary"
                  leadingIcon={<Icon as={UserPlus} boxSize={4} />}
                  onClick={onAddUser}
                >
                  Add a user Ploy is missing
                </Button>
              )}
            </VStack>
          ) : (
            <>
              {/* applied entitlement corrections */}
              {hasCorrections && (
                <VStack align="stretch" spacing={3}>
                  <Text
                    fontSize="xs"
                    fontWeight={600}
                    color="gray.500"
                    textTransform="uppercase"
                    letterSpacing="0.04em"
                  >
                    Applied corrections
                  </Text>
                  {correctionCards.map((change) => (
                    <ChangeCard key={change.id} change={change} />
                  ))}
                </VStack>
              )}

              {/* flagged-for-removal record (not a correction) */}
              {hasFlagged && (
                <VStack align="stretch" spacing={3}>
                  <HStack spacing={1.5}>
                    <Icon as={Flag} boxSize={3.5} color="warning.600" />
                    <Text
                      fontSize="xs"
                      fontWeight={600}
                      color="gray.500"
                      textTransform="uppercase"
                      letterSpacing="0.04em"
                    >
                      Flagged for removal
                    </Text>
                  </HStack>
                  <Text fontSize="xs" color="gray.500">
                    These entitlements are flagged for the remediation task. They
                    are not entitlement corrections.
                  </Text>
                  {flaggedRecords.map((change) => (
                    <ChangeCard key={change.id} change={change} />
                  ))}
                </VStack>
              )}

              {/* accounts removed because they don't exist at source */}
              {hasRemoved && (
                <VStack align="stretch" spacing={3}>
                  <HStack spacing={1.5}>
                    <Icon as={UserMinus} boxSize={3.5} color="error.600" />
                    <Text
                      fontSize="xs"
                      fontWeight={600}
                      color="gray.500"
                      textTransform="uppercase"
                      letterSpacing="0.04em"
                    >
                      Removed, not in source
                    </Text>
                  </HStack>
                  {removedRecords.map((change) => (
                    <ChangeCard key={change.id} change={change} />
                  ))}
                </VStack>
              )}
            </>
          )}
        </VStack>
      </Flex>
    );
  },
);

FixPanel.displayName = "FixPanel";

export default FixPanel;
